/**
 * Matches a pile of dropped report/metadata files onto review rows, so a
 * 200-row import is one folder drop instead of 400 trips through a file
 * picker.
 *
 * Two passes, in order of how much the match can be trusted:
 *
 *   1. The filename the sheet itself names for that row — "Report File Name"
 *      and "Report Metadata File Name" are already columns in the template
 *      (csvStringToRows keeps them now rather than discarding them), so where
 *      someone filled them in, the match is stated rather than inferred.
 *   2. Otherwise the row's own identifiers found inside the filename, since
 *      labs almost always name a report after the registration number or QR
 *      code it belongs to.
 *
 * Nothing is assigned unless the match is unique from BOTH sides — one file
 * naming one row, that row named by no other file. A file matching two rows,
 * or two files matching one row, is reported rather than resolved by
 * arbitrary tie-break: attaching the wrong lab report to an order is far
 * worse than leaving it for a person to attach by hand.
 */

export const REPORT_FIELD = "reportFile";
export const METADATA_FIELD = "reportMetadataFile";

// The sheet column naming each file, keyed by the grid field it fills.
export const FILE_NAME_FIELDS = Object.freeze({
  [REPORT_FIELD]: "reportFileName",
  [METADATA_FIELD]: "reportMetadataFileName",
});

// Row columns worth looking for inside a filename, most specific first — the
// order decides which row wins when a filename happens to contain two rows'
// identifiers (a long registration number beats a short batch id).
export const MATCH_KEY_FIELDS = Object.freeze([
  "sampleRegistrationNumber", "qrCodeId", "sampleSealNo", "batchId",
]);

// Short identifiers match far too much — "12" appears in half the filenames
// in any real folder — so anything shorter than this is ignored for pass 2.
// An exact filename from the sheet is unaffected; this only limits guessing.
const MIN_KEY_LENGTH = 4;

const METADATA_SUFFIX = /[ _-]*(metadata|meta)$/i;
const REPORT_SUFFIX = /[ _-]*(report)$/i;

export const isPdfFile = (file) => {
  if (!file) return false;
  const name = String(file.name || file.webkitRelativePath || "").toLowerCase();
  const isPdf = name.endsWith(".pdf") || file.type === "application/pdf";
  const hasSize = file.size === undefined || file.size > 0;
  return isPdf && hasSize;
};

// Folder drops arrive with webkitRelativePath set ("reports/ORD-1.pdf"), so
// the path has to come off before anything is compared.
export function splitName(name) {
  const clean = String(name || "").split(/[\\/]/).pop().trim();
  const dot = clean.lastIndexOf(".");
  if (dot <= 0) return { full: clean, base: clean, ext: "" };
  return { full: clean, base: clean.slice(0, dot), ext: clean.slice(dot + 1).toLowerCase() };
}

// Punctuation is where filenames and sheet values disagree most ("ORD-1023"
// vs "ORD_1023" vs "ORD 1023"), and it never carries meaning here.
export function normalize(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Report or metadata, from the filename's own suffix/keywords. Only PDFs allowed. */
export function roleOf({ base, ext }) {
  if (ext !== "pdf") return null;
  if (METADATA_SUFFIX.test(base) || /(?:^|[ _-])(?:metadata|meta)(?:[ _-]|$)/i.test(base)) return METADATA_FIELD;
  return REPORT_FIELD;
}

// What's left of a filename once the role words are stripped — "ORD-1023" out
// of "ORD-1023_metadata.pdf", so the same row matches both of its files.
function keyOf(base) {
  return normalize(String(base).replace(METADATA_SUFFIX, "").replace(REPORT_SUFFIX, ""));
}

const targetKey = (rowId, field) => `${rowId}::${field}`;

/**
 * Resolves a set of file -> target claims down to assignments, keeping only
 * the ones unique from both sides and describing every one it drops.
 */
function resolveClaims(entries) {
  const byTarget = new Map();
  entries.forEach((entry) => {
    entry.targets.forEach((target) => {
      const key = targetKey(target.rowId, target.field);
      if (!byTarget.has(key)) byTarget.set(key, []);
      byTarget.get(key).push(entry);
    });
  });

  const assigned = [];
  const problems = [];
  entries.forEach((entry) => {
    if (!entry.targets.length) return;
    if (entry.targets.length > 1) {
      problems.push({ entry, reason: `matches ${entry.targets.length} rows — attach it by hand` });
      return;
    }
    const target = entry.targets[0];
    const claimants = byTarget.get(targetKey(target.rowId, target.field)) || [];
    if (claimants.length > 1) {
      const others = claimants.filter((c) => c !== entry).map((c) => c.full);
      problems.push({ entry, reason: `also matched by ${others.join(", ")} — attach one by hand` });
      return;
    }
    assigned.push({ entry, target });
  });

  return { assigned, problems };
}

/** Every row a filename's identifiers point at, narrowed to the longest identifier that hit. */
function rowsByIdentifier(entry, rows) {
  let best = 0;
  const hits = [];
  rows.forEach((row) => {
    let longest = 0;
    MATCH_KEY_FIELDS.forEach((field) => {
      const key = normalize(row[field]);
      if (key.length >= MIN_KEY_LENGTH && entry.key.includes(key) && key.length > longest) {
        longest = key.length;
      }
    });
    if (!longest) return;
    hits.push({ rowId: row.rowId, length: longest });
    if (longest > best) best = longest;
  });
  return hits.filter((hit) => hit.length === best).map((hit) => hit.rowId);
}

/**
 * @param files  the dropped File objects, in any order
 * @param rows   the current review rows
 * @returns { rows, touched, summary, pdfFiles }
 */
export default function matchFilesToRows(files, rows) {
  const allFiles = Array.from(files || []);
  const zeroByteFiles = allFiles.filter((f) => f.size === 0);
  const pdfFiles = allFiles.filter(isPdfFile);
  const ignoredNonPdf = allFiles.length - pdfFiles.length - zeroByteFiles.length;

  const entries = pdfFiles.map((file) => {
    const parts = splitName(file.webkitRelativePath || file.name);
    return { file, ...parts, role: roleOf(parts), key: keyOf(parts.base), targets: [] };
  });

  // ---- pass 1: the filename the sheet names for that row ----
  const expected = new Map(); // normalized name -> [{ rowId, field }]
  rows.forEach((row) => {
    Object.entries(FILE_NAME_FIELDS).forEach(([field, nameField]) => {
      const raw = String(row[nameField] || "").trim();
      if (!raw) return;
      const parts = splitName(raw);
      // Both spellings, because a sheet cell may or may not carry the
      // extension and either should find the file on disk.
      new Set([normalize(parts.full), normalize(parts.base)]).forEach((key) => {
        if (!key) return;
        if (!expected.has(key)) expected.set(key, []);
        expected.get(key).push({ rowId: row.rowId, field });
      });
    });
  });

  entries.forEach((entry) => {
    const seen = new Set();
    [normalize(entry.full), normalize(entry.base)].forEach((key) => {
      (expected.get(key) || []).forEach((target) => {
        const id = targetKey(target.rowId, target.field);
        if (seen.has(id)) return;
        seen.add(id);
        entry.targets.push(target);
      });
    });
  });

  const named = resolveClaims(entries.filter((entry) => entry.targets.length));

  // ---- pass 2: the row's identifiers inside the filename ----
  const decided = new Set([
    ...named.assigned.map((a) => a.entry),
    ...named.problems.map((p) => p.entry),
  ]);
  const guessable = entries.filter((entry) => !decided.has(entry));
  const unreadable = [];
  guessable.forEach((entry) => {
    entry.targets = [];
    if (!entry.role) {
      unreadable.push({ entry, reason: "not a recognized PDF report or metadata file" });
      return;
    }
    if (!entry.key) {
      unreadable.push({ entry, reason: "filename has nothing to match on" });
      return;
    }
    entry.targets = rowsByIdentifier(entry, rows).map((rowId) => ({ rowId, field: entry.role }));
  });

  const guessed = resolveClaims(guessable.filter((entry) => !unreadable.some((u) => u.entry === entry)));

  // ---- apply ----
  const assignments = new Map(); // rowId -> { field: file }
  [...named.assigned, ...guessed.assigned].forEach(({ entry, target }) => {
    if (!assignments.has(target.rowId)) assignments.set(target.rowId, {});
    assignments.get(target.rowId)[target.field] = entry.file;
  });

  let replaced = 0;
  const touched = [];
  const nextRows = rows.map((row) => {
    const attach = assignments.get(row.rowId);
    if (!attach) return row;
    const fields = Object.keys(attach);
    fields.forEach((field) => { if (row[field]) replaced += 1; });
    touched.push({ rowId: row.rowId, fields });
    return { ...row, ...attach };
  });

  const counted = (field) => [...assignments.values()].filter((a) => a[field]).length;
  const missing = (field) => nextRows.filter((row) => !row[field]).length;
  const unmatched = [
    ...named.problems, ...guessed.problems, ...unreadable,
    ...guessable
      .filter((entry) => !entry.targets.length && !unreadable.some((u) => u.entry === entry))
      .map((entry) => ({ entry, reason: "no row names it, and no row's id appears in it" })),
  ]
    .map(({ entry, reason }) => ({
      name: entry.full,
      file: entry.file,
      defaultRole: entry.role || REPORT_FIELD,
      reason,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    rows: nextRows,
    touched,
    pdfFiles,
    summary: {
      files: allFiles.length,
      pdfFiles: pdfFiles.length,
      ignoredNonPdf,
      reports: counted(REPORT_FIELD),
      metadata: counted(METADATA_FIELD),
      rowsTouched: touched.length,
      replaced,
      unmatched,
      allPdfs: pdfFiles,
      missingReports: missing(REPORT_FIELD),
      missingMetadata: missing(METADATA_FIELD),
    },
  };
}
